/* global FormData */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import OImage from "components/reusable/OImage";
import { useForm } from "react-hook-form";
import OButton from "components/reusable/OButton";
import { apiGet, apiPost, apiPut, apiDelete } from "utils/apiFetch";
import { useLocation, useNavigate } from "react-router-dom";
import apiPath from "../../utils/apiPath";
import useToastContext from "hooks/useToastContext";
import OInputField from "components/reusable/OInputField";
import formValidation from "utils/formValidation";
import { BsTrash } from "react-icons/bs";
import OTextArea from "components/reusable/OTextArea";
import { BLOG_TYPES } from "utils/constants";
import { Autocomplete, TextField } from "@mui/material";
import BlogRichEditor from "components/reusable/BlogRichEditor";
import BlogFaqSection from "./BlogFaqSection";
const BlogPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const item = location?.state ?? "";
  const [content, setContent] = useState("");
  const [contentAr, setContentAr] = useState("");
  const [blogTypes, setBlogTypes] = useState([]);
  const [isRedirect, setRedirect] = useState(false);
  const [profilePic, setProfilePic] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [initialFaqIds, setInitialFaqIds] = useState([]);
  const navigate = useNavigate();
  const notification = useToastContext();

  const {
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    shouldFocusError: true,
  });

  const handleFileChange = (e) => {
    const image = e?.target?.files[0];
    const isValidImage = validateFiles(image);
    if (isValidImage) {
      setProfilePic(image);
    } else {
      setProfilePic(null);
    }
  };

  const validateFiles = (newProfilePic) => {
    const supportedTypes = ["image/jpeg", "image/jpg", "image/png"];

    if (!supportedTypes.includes(newProfilePic.type)) {
      return "Only jpeg, jpg, and png are supported.";
    }

    return true;
  };

  const getBack = () => {
    setRedirect(true);
    navigate("/blogs");
  };

  useEffect(() => {
    if (item?._id) setValue("title", item?.title);
    setValue("titleAr", item?.titleAr);
    setProfilePic(item?.image);
    setContent(item?.description);
    setContentAr(item?.descriptionAr);
    setBlogTypes(item?.blogTypes);
    reset(item);
  }, []);

  useEffect(() => {
    const loadFaqs = async () => {
      if (!item?._id) return;
      try {
        const result = await apiGet(apiPath.blogFaqs, {
          blogId: item._id,
          page_size: 100,
        });
        if (result?.data?.success) {
          const docs = result?.data?.results?.docs || [];
          const mapped = docs.map((faq) => ({
            _id: faq._id,
            title: faq.title || "",
            titleAr: faq.titleAr || "",
            content: faq.content || "",
            contentAr: faq.contentAr || "",
          }));
          setFaqs(mapped);
          setInitialFaqIds(mapped.map((f) => f._id).filter(Boolean));
        }
      } catch (error) {
        console.error("error loading blog faqs==>>>>", error.message);
      }
    };
    loadFaqs();
  }, [item?._id]);

  const isFaqEmpty = (faq) =>
    !faq.title?.trim() &&
    !faq.titleAr?.trim() &&
    !faq.content?.trim() &&
    !faq.contentAr?.trim();

  const isFaqComplete = (faq) =>
    !!faq.title?.trim() &&
    !!faq.titleAr?.trim() &&
    !!faq.content?.trim() &&
    !!faq.contentAr?.trim();

  const validateFaqs = () => {
    const nonEmpty = faqs.filter((faq) => !isFaqEmpty(faq));
    if (nonEmpty.some((faq) => !isFaqComplete(faq))) {
      notification.error(
        "Please fill Question, Question (Arabic), Answer, and Answer (Arabic) for each FAQ.",
      );
      return false;
    }
    return true;
  };

  const syncFaqs = async (blogId) => {
    const activeFaqs = faqs.filter((faq) => !isFaqEmpty(faq));
    const currentIds = activeFaqs.map((f) => f._id).filter(Boolean);
    const toDelete = initialFaqIds.filter((id) => !currentIds.includes(id));

    for (const id of toDelete) {
      await apiDelete(`${apiPath.blogFaqs}/${id}`);
    }

    for (const faq of activeFaqs) {
      const payload = {
        blogId,
        title: faq.title.trim(),
        titleAr: faq.titleAr.trim(),
        content: faq.content.trim(),
        contentAr: faq.contentAr.trim(),
      };
      if (faq._id) {
        await apiPut(`${apiPath.blogFaqs}/${faq._id}`, payload);
      } else {
        await apiPost(apiPath.blogFaqs, payload);
      }
    }
  };

  const onSubmit = async (data) => {
    try {
      if (profilePic != null) {
        if (!isEmpty(content)) {
          if (!isEmpty(contentAr)) {
            if (!validateFaqs()) return;

            const formData = new FormData();
            formData.append("title", data.title);
            formData.append("titleAr", data.titleAr);
            formData.append("keyword", data.keyword);
            formData.append("keywordAr", data.keywordAr);
            formData.append("metaDescription", data.metaDescription);
            formData.append("metaDescriptionAr", data.metaDescriptionAr);
            formData.append("metaTitle", data.metaTitle);
            formData.append("metaTitleAr", data.metaTitleAr);
            formData.append("description", content);
            formData.append("descriptionAr", contentAr);
            formData.append("image", profilePic);
            formData.append("slug", data.slug);
            formData.append("slugAr", data.slugAr);
            formData.append("blogTypes", JSON.stringify(blogTypes));

            if (isEmpty(item)) {
              const path = apiPath.ListBlog;
              const result = await apiPost(path, formData);
              if (result?.data?.success) {
                const blogId = result?.data?.results?._id;
                if (blogId) {
                  await syncFaqs(blogId);
                }
                setRedirect(true);
                notification.success(result.data.message);
              } else {
                notification.error(result.data.message);
              }
            } else {
              const path = apiPath.ListBlog + "/" + item?._id;
              const result = await apiPut(path, formData);
              if (result?.data?.success) {
                await syncFaqs(item._id);
                setRedirect(true);
                notification.success(result.data.message);
              } else {
                notification.error(result.data.message);
              }
            }
          } else {
            notification.error("Please enter content in arabic.");
          }
        } else {
          notification.error("Please enter content.");
        }
      } else {
        notification.error("Please select an image.");
      }
    } catch (error) {
      console.error("error in get all users list==>>>>", error.message);
      notification.error(error?.response?.data?.message || error.message);
    }
  };

  if (isRedirect) {
    return navigate("/blogs");
  }

  return (
    <div className="py-4 px-4 md:px-8 bg-gray-50">
      <div className="border-0 relative flex flex-col w-full  outline-none focus:outline-none">
        <h2 className="font-medium text-black dark:text-white mb-3 text-xl">
          {isEmpty(item) ? t("ADD_BLOG") : "Edit Blog"}
        </h2>
        <div className="flex gap-4 items-start w-full">
          <div className="w-1/2 rounded-lg border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark p-7">
            <OInputField
              wrapperClassName=""
              name="title"
              inputLabel={
                <>
                  {t("O_TITLE")}
                  <span className="text-red-500">*</span>
                </>
              }
              type="text"
              autoFocus
              register={register("title", formValidation.title)}
              errors={errors}
            />
            <OInputField
              wrapperClassName=""
              name="titleAr"
              inputLabel={
                <>
                  {t("O_TITLE") + t("O_ARABIC")}
                  <span className="text-red-500">*</span>
                </>
              }
              type="text"
              register={register("titleAr", formValidation.titleAr)}
              errors={errors}
            />
            {item && (
              <>
                <OInputField
                  wrapperClassName="relative z-0   w-full group"
                  name="slug"
                  inputLabel={
                    <>
                      {t("O_SLUG")}
                      <span className="text-red-500">*</span>
                    </>
                  }
                  type="text"
                  register={register("slug", formValidation.slug)}
                  errors={errors}
                />

                <OInputField
                  wrapperClassName="relative z-0   w-full group"
                  name="slugAr"
                  inputLabel={
                    <>
                      {t("O_SLUG") + t("O_ARABIC")}
                      <span className="text-red-500">*</span>
                    </>
                  }
                  type="text"
                  register={register("slugAr", formValidation.slugAr)}
                  errors={errors}
                />
              </>
            )}

            <OInputField
              wrapperClassName="relative z-0   w-full group"
              name="metaTitle"
              inputLabel={t("STATIC_TITLE")}
              type="text"
              register={register("metaTitle")}
              errors={errors}
            />

            <OInputField
              wrapperClassName="relative z-0   w-full group"
              name="metaTitleAr"
              inputLabel={
                <>
                  {t("STATIC_TITLE")} {t("O_ARABIC")}
                </>
              }
              type="text"
              register={register("metaTitleAr")}
              errors={errors}
            />

            <OTextArea
              wrapperClassName="relative z-0   w-full group"
              name="metaDescription"
              inputLabel={t("STATIC_DESCRIPTION")}
              type="text"
              register={register("metaDescription")}
              errors={errors}
            />

            <OTextArea
              wrapperClassName="relative z-0 w-full group"
              name="metaDescriptionAr"
              inputLabel={
                <>
                  {t("STATIC_DESCRIPTION")} {t("O_ARABIC")}
                </>
              }
              type="text"
              register={register("metaDescriptionAr")}
              errors={errors}
            />

            <OTextArea
              wrapperClassName="relative z-0 w-full group"
              name="keyword"
              inputLabel={t("KEYWORD")}
              type="text"
              register={register("keyword")}
              errors={errors}
            />

            <OTextArea
              wrapperClassName="relative z-0 w-full group"
              name="keywordAr"
              inputLabel={
                <>
                  {t("KEYWORD")} {t("O_ARABIC")}
                </>
              }
              type="text"
              register={register("keywordAr")}
              errors={errors}
            />
            <div className="flex flex-col text-sm rounded-md">
              <p className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                {t("OFFERS_DESCRIPTION")}
              </p>
              <div className="shadow-md mb-5 rounded-[4px]  hover:outline-none focus:outline-none hover:border-yellow-500 ">
                <BlogRichEditor value={content} onChange={setContent} />
              </div>
              <p className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                {t("OFFERS_DESCRIPTION") + t("O_ARABIC")}
              </p>
              <div className="shadow-md mb-5 rounded-[4px]  hover:outline-none focus:outline-none hover:border-yellow-500 ">
                <BlogRichEditor value={contentAr} onChange={setContentAr} />
              </div>
            </div>
            <div className="flex flex-col text-sm rounded-md">
              <p className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                {t("BLOG_TYPES")}
              </p>
              <Autocomplete
                multiple
                options={BLOG_TYPES}
                getOptionLabel={(option) => option.key || ""}
                value={BLOG_TYPES.filter((item) =>
                  blogTypes?.includes(item.value),
                )}
                onChange={(_, newValue) => {
                  setBlogTypes(newValue?.map((item) => item.value));
                }}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("PLEASE_SELECT_BLOG_TYPES")}
                    placeholder={t("PLEASE_SELECT_BLOG_TYPES")}
                  />
                )}
              />
            </div>
            <BlogFaqSection faqs={faqs} onChange={setFaqs} />
            <div className="flex items-center justify-center p-6 border-t border-solid border-slate-200 rounded-b">
              <button
                className="text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6  ease-linear transition-all duration-150"
                type="button"
                onClick={getBack}
              >
                {t("O_BACK")}
              </button>
              <div className="text-center">
                <OButton
                  label={isEmpty(item) ? t("O_ADD") : t("SAVE")}
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                />
              </div>
            </div>
          </div>

          <div className="w-1/2 rounded-lg border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark p-7 relative">
            <div className="">
              <h3 className="font-medium text-black dark:text-white mb-3 text-lg">
                {t("O_ADMIN_BLOG_IMAGE_SIZE_SUGGESTION")}
              </h3>
            </div>

            {!profilePic ? (
              <div
                id="FileUpload"
                className="relative mb-5.5 block w-full cursor-pointer appearance-none rounded border-2 border-dashed border-primary bg-gray py-5 px-4 dark:bg-meta-4 sm:py-7.5"
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="absolute inset-0 z-50 m-0 h-full w-full cursor-pointer p-0 opacity-0 outline-none"
                />
                <div className="flex flex-col items-center justify-center space-y-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full border border-stroke bg-white dark:border-strokedark dark:bg-boxdark">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M1.99967 9.33337C2.36786 9.33337 2.66634 9.63185 2.66634 10V12.6667C2.66634 12.8435 2.73658 13.0131 2.8616 13.1381C2.98663 13.2631 3.1562 13.3334 3.33301 13.3334H12.6663C12.8431 13.3334 13.0127 13.2631 13.1377 13.1381C13.2628 13.0131 13.333 12.8435 13.333 12.6667V10C13.333 9.63185 13.6315 9.33337 13.9997 9.33337C14.3679 9.33337 14.6663 9.63185 14.6663 10V12.6667C14.6663 13.1971 14.4556 13.7058 14.0806 14.0809C13.7055 14.456 13.1968 14.6667 12.6663 14.6667H3.33301C2.80257 14.6667 2.29387 14.456 1.91879 14.0809C1.54372 13.7058 1.33301 13.1971 1.33301 12.6667V10C1.33301 9.63185 1.63148 9.33337 1.99967 9.33337Z"
                        fill="#3C50E0"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M7.5286 1.52864C7.78894 1.26829 8.21106 1.26829 8.4714 1.52864L11.8047 4.86197C12.0651 5.12232 12.0651 5.54443 11.8047 5.80478C11.5444 6.06513 11.1223 6.06513 10.8619 5.80478L8 2.94285L5.13807 5.80478C4.87772 6.06513 4.45561 6.06513 4.19526 5.80478C3.93491 5.54443 3.93491 5.12232 4.19526 4.86197L7.5286 1.52864Z"
                        fill="#3C50E0"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M7.99967 1.33337C8.36786 1.33337 8.66634 1.63185 8.66634 2.00004V10C8.66634 10.3682 8.36786 10.6667 7.99967 10.6667C7.63148 10.6667 7.33301 10.3682 7.33301 10V2.00004C7.33301 1.63185 7.63148 1.33337 7.99967 1.33337Z"
                        fill="#3C50E0"
                      />
                    </svg>
                  </span>
                  <p className="font-medium text-sm">
                    <span className="text-primary">Click to upload</span>
                    {/* or drag and drop */}
                  </p>
                  {/* <p className='mt-1.5 font-medium text-sm'>
                    SVG, PNG, JPG or GIF
                  </p>
                  <p className='font-medium text-sm'>(max, 800 X 800px)</p> */}
                </div>
              </div>
            ) : (
              <div className="relative2">
                <button
                  className="  text-black rounded-full text-xxl leading-none font-extralight outline-none focus:outline-none absolute top-2 right-[12px]  bg-white w-[40px] h-[40px]"
                  onClick={() => setProfilePic("")}
                >
                  <span className=" text-red-500  text-lg " title="Close">
                    <BsTrash />
                  </span>
                </button>
                <OImage
                  src={
                    typeof profilePic === "object"
                      ? URL.createObjectURL(profilePic)
                      : profilePic
                  }
                />
              </div>
            )}
          </div>
        </div>

        {/* <div className='relative p-6 flex-auto'>
          <h3 className='text-xl font-semibold'>{t('ADD_BLOG')}</h3>
          <div className='flex justify-center p-4'>
            <div className='flex-col border bg-white px-6 py-14 shadow-md rounded-[4px] max-w-[1088px] w-full'>
              <div className='flex justify-center pb-4'>
                <strong>{t('O_ADMIN_BLOG_IMAGE_SIZE_SUGGESTION')}</strong>
              </div>
              <div className='mb-8 flex justify-center'>
                <div className='w-full border h-[576px]  max-w-[1024px] relative'>
                  {profilePic === null && (
                    <span
                      title='1024*576'
                      className='p-3 cursor-pointer flex justify-center pt-[231px]'
                      onClick={() => profilePicRef?.current?.click()}
                    >
                      <GrAdd className='h-16 w-16 flex justify-center' />
                    </span>
                  )}
                  <input
                    type='file'
                    accept='image/png, image/jpg, image/jpeg'
                    name='image'
                    ref={profilePicRef}
                    onChange={handleFileChange}
                    className='hidden '
                  />

                  {profilePic && (
                    <OImage
                      src={profilePic ? URL.createObjectURL(profilePic) : ''}
                      fallbackUrl='/images/imagesBanner.png'
                      // className='w-24 h-24'
                      className='w-full border h-[300px] relative'
                      alt=''
                    />
                  )}
                  <span className=' align-center absolute top-0 end-0 p-3 rounded bg-black text-white cursor-pointer'>
                    <AiFillCamera
                      title='1024*576'
                      onClick={() => profilePicRef?.current?.click()}
                    />
                  </span>
                </div>
              </div>
              {profilePicError && <ErrorMessage message={profilePicError} />}

              <OInputField
                wrapperClassName=''
                name='title'
                inputLabel={
                  <>
                    {t('O_TITLE')}
                    <span className='text-red-500'>*</span>
                  </>
                }
                type='text'
                autoFocus
                register={register('title', formValidation.title)}
                errors={errors}
              />
              <OInputField
                wrapperClassName=''
                name='titleAr'
                inputLabel={
                  <>
                    {t('O_TITLE') + t('O_ARABIC')}
                    <span className='text-red-500'>*</span>
                  </>
                }
                type='text'
                autoFocus
                register={register('titleAr', formValidation.titleAr)}
                errors={errors}
              />
              <div className='flex flex-col text-sm rounded-md'>
                <div className='shadow-md mb-5 rounded-[4px]  hover:outline-none focus:outline-none hover:border-yellow-500 '>
                  <ReactQuill
                    theme='snow'
                    value={content}
                    onChange={setContent}
                  />
                </div>
              </div>
              <div className='flex flex-col text-sm rounded-md'>
                <div className='shadow-md mb-5 rounded-[4px]  hover:outline-none focus:outline-none hover:border-yellow-500 '>
                  <ReactQuill
                    theme='snow'
                    value={contentAr}
                    onChange={setContentAr}
                  />
                </div>
              </div>
              <div className='flex items-center justify-center p-6 border-t border-solid border-slate-200 rounded-b'>
                <button
                  className='text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6  ease-linear transition-all duration-150'
                  type='button'
                  onClick={getBack}
                >
                  {t('O_BACK')}
                </button>
                <div className='text-center'>
                  <OButton
                    label={<>{t('O_ADD')}</>}
                    type='button'
                    onClick={handleSubmit(onSubmit)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default BlogPage;
